import { useMemo, useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useApolloClient } from "@apollo/client/react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { UniversalEntityPageProps } from "../types";
import type { AuthenticatedUser } from "@/lib/types/unified";
import type { PaginationModeContextValue } from "../contexts/types";
import { useDataProcessing } from "./useDataProcessing";
import { detectGroupableFields } from "@/lib/utils/crm/grouping-strategies";
import { useContextHooks } from "./useContextHooks";
import { useEntityState } from "./useEntityState";
import { useQueryParams } from "./useQueryParams";
import { useDataFetching } from "./useDataFetching";
import { useFTSEnhancement } from "./useFTSEnhancement";
import { useDataNormalization } from "./useDataNormalization";
import { useInitialLoadTracking } from "./useInitialLoadTracking";
import { useInfiniteScroll } from "./useInfiniteScroll";
import { useEntityPageStableCallbacks } from "./useEntityPageStableCallbacks";
import { useEntityPageMemoizedState } from "./useEntityPageMemoizedState";

interface UseUniversalEntityPageDataProps<
  T extends BaseEntity,
> extends UniversalEntityPageProps<T> {
  appUser: AuthenticatedUser;
  paginationModeState: PaginationModeContextValue;
}

/**
 * Data fetching and processing logic for UniversalEntityPage.
 * Composes multiple focused sub-hooks for better organization and maintainability.
 * Now uses FilterContext as the single source of truth for all filter state
 * and paginationModeState for pagination management.
 *
 * Stable callbacks are in useEntityPageStableCallbacks.
 * Memoized state objects are in useEntityPageMemoizedState.
 */
export function useUniversalEntityPageData<T extends BaseEntity>(
  {
    entityName,
    config,
    hooks,
    filterPreset = "all",
    customFilters = {},
    enableFTS = false,
    paginationModeState,
  }: UseUniversalEntityPageDataProps<T>,
  appUser: AuthenticatedUser
) {
  // ============================================================================
  // NAVIGATION & ROUTING
  // ============================================================================
  const router = useRouter();
  const searchParams = useSearchParams();
  const apolloClient = useApolloClient();

  // CRITICAL: Compute activeViewId BEFORE any conditional hook calls
  // This ensures stable hook ordering regardless of URL params
  const activeViewId = searchParams?.get("view") || null;

  // ============================================================================
  // CONTEXT INITIALIZATION
  // ============================================================================
  const { context, filterContext, savedViewsState } = useContextHooks<T>();

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  const entityState = useEntityState<T>();

  // ============================================================================
  // QUERY PARAMETER BUILDING
  // ============================================================================
  const { queryParams } = useQueryParams({
    filterContext,
    pagination: paginationModeState.paginationMode,
    customFilters,
    filterPreset,
    appUser,
    entityName,
    config,
    enableFTS,
  });

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  const { entitiesQueryRaw, createMutation, updateMutation, deleteMutation } = useDataFetching({
    hooks,
    queryParams,
    config,
    activeViewId,
  });

  // ============================================================================
  // DATA PROCESSING
  // ============================================================================
  const { filteredData, isLoading, error, refetch, networkStatus } = useDataProcessing(
    entitiesQueryRaw,
    filterContext?.filters.search || "",
    {},
    config,
    entityName
  );

  // ============================================================================
  // FTS SEARCH ENHANCEMENT
  // ============================================================================
  const { ftsTotalCount, ftsIsLoading, ftsError, finalData, processedData: ftsProcessedData } =
    useFTSEnhancement({
      enableFTS,
      entityName,
      filterContext,
      filteredData,
    });

  // ============================================================================
  // DATA NORMALIZATION
  // ============================================================================
  const { data } = useDataNormalization({
    entitiesQueryRaw,
    enableFTS,
    finalData,
    ftsTotalCount,
    filterContext,
    displayData: ftsProcessedData,
  });

  // ============================================================================
  // INITIAL LOAD TRACKING
  // Placed after normalization so we can pass the actual normalized data.
  // ============================================================================
  const { hasLoadedOnce, accumulatedData, setAccumulatedData } = useInitialLoadTracking({
    data,
    filterContext,
  });

  // ============================================================================
  // INFINITE SCROLL
  // ============================================================================
  const { displayData: infiniteScrollDisplayData, loadMore: loadMoreCursor, hasNextPage } =
    useInfiniteScroll({
      data,
      pagination: paginationModeState.paginationMode,
      accumulatedData,
      setAccumulatedData,
      filterContext,
    });

  // ============================================================================
  // GROUPABLE FIELDS
  // ============================================================================
  const groupableFields = useMemo(() => detectGroupableFields(config), [config]);

  // Stable reference: memoize currentFilters so it doesn't create a new object every render
  const currentFilters = useMemo(
    () => ({
      filters: filterContext?.filters.simple || {},
      search: filterContext?.filters.search || undefined,
      advancedFilters: filterContext?.filters.advanced || [],
      filterOperator: filterContext?.uiState?.filterOperator || "AND",
    }),
    [
      filterContext?.filters.simple,
      filterContext?.filters.search,
      filterContext?.filters.advanced,
      filterContext?.uiState?.filterOperator,
    ]
  );

  // ============================================================================
  // STABLE CALLBACKS (extracted to useEntityPageStableCallbacks)
  // ============================================================================
  const {
    stableNoop,
    stableNoopObj,
    setActiveViewId,
    applySavedView,
    setPagination,
    stableFilterHelpersFallback,
  } = useEntityPageStableCallbacks({
    router,
    filterContext,
    paginationModeState,
  });

  // Destructure stable function refs from paginationModeState.
  const { setPage, setPageSize, setCursor } = paginationModeState;

  // ============================================================================
  // MEMOIZED STATE OBJECTS (extracted to useEntityPageMemoizedState)
  // ============================================================================
  const {
    navigation,
    filterState,
    paginationState,
    mutations,
    dataState,
  } = useEntityPageMemoizedState<T>({
    filterContext,
    paginationModeState,
    currentFilters,
    stableNoop,
    stableNoopObj,
    activeViewId,
    setActiveViewId,
    applySavedView,
    setPagination,
    filteredData,
    ftsProcessedData,
    infiniteScrollDisplayData,
    isLoading,
    networkStatus,
    error,
    refetch,
    data,
    groupableFields,
    hasLoadedOnce,
    ftsIsLoading,
    ftsError,
    hasNextPage,
    loadMoreCursor,
    setCursor,
    setPage,
    createMutation,
    updateMutation,
    deleteMutation,
    router,
    searchParams,
    apolloClient,
  });

  const filterHelpers = filterContext?.helpers || stableFilterHelpersFallback;

  return {
    navigation,
    entityState: { ...entityState },
    filterState,
    paginationState,
    filterHelpers,
    queryParams,
    mutations,
    dataState,
  };
}
